import { capitalize } from 'lodash';

import { CATEGORY_NAMES } from '../product-list-page-constants';

import {
  parseTireSize,
  parseWheelSize
} from '../../../shared/utils/fitment/fitment';
import {
  formatTireSizeParts,
  formatWheelSizeParts
} from '../../../shared/utils/format/format';
import { localStorage } from '../../../shared/utils/storage/storage';

import { PRODUCT_TYPES, QUERY_TYPES } from '../../../shared/constants/products';
import { SUGGESTED_SELLING_VALUES } from '../../../shared/constants/storage-keys';
import { DEFAULT_SUGGESTED_SELLING_VALUES } from '../../../shared/constants/suggested-selling';

function getBrandDisplayText(brandParam) {
  return brandParam.split('-').map(capitalize).join(' ');
}

export function getDescription({
  brand,
  category,
  productType,
  queryParams,
  queryType,
  selectedFilters,
  selectedFitment,
  tireSizes,
  wheelSizes
}) {
  const site = window.siteName;
  const { frontTireSize, frontWheelDiameter, rearTireSize, rearWheelDiameter } =
    selectedFitment || {};

  if (
    queryType === QUERY_TYPES.BRAND_SEARCH &&
    queryParams?.cat &&
    category?.name &&
    selectedFilters?.[0]
  ) {
    return `Find the best prices and selection of ${category.name.replace(
      /\sTires|\sWheels$/i,
      ''
    )} ${selectedFilters[0]?.valueName} right here!`;
  }
  if (category?.name === CATEGORY_NAMES.TPMS_SENSORS) {
    return `Find the best tire pressure monitoring systems (TPMS) for your car at ${site}.`;
  }
  if (category?.name === CATEGORY_NAMES.TIRE_CHAIN_TENSIONERS) {
    return `Winter is coming! Find quality tire chain tensioners for your tires at ${site}.`;
  }
  if (category?.name === CATEGORY_NAMES.TIRE_CHAINS) {
    return `Winter is coming! Buy high quality tire chains from ${site}.`;
  }
  if (category?.name === CATEGORY_NAMES.WINDSHIELD_WIPERS) {
    return `Wiper blades are engineered to deliver high performance. Shop wipers at ${site}.`;
  }

  if (
    (queryType === QUERY_TYPES.CATEGORY_SEARCH ||
      queryType === QUERY_TYPES.BRAND_SEARCH) &&
    category?.name
  ) {
    return `Find ${category?.name} | ${site}`;
  }

  if (
    queryType === QUERY_TYPES.SIZE_SEARCH &&
    productType === PRODUCT_TYPES.TIRES
  ) {
    const { front, rear } = tireSizes || {};
    let sizeText = formatTireSizeParts(front) || frontTireSize;
    if (rear) {
      sizeText = `${formatTireSizeParts(front).replace(
        /\s+/g,
        ''
      )} X ${formatTireSizeParts(rear).replace(/\s+/g, '')}`;
    } else if (rearTireSize !== frontTireSize) {
      sizeText = `${frontTireSize} X ${rearTireSize}`;
    }
    return `Find our selection of ${sizeText}${
      brand ? ` ${getBrandDisplayText(brand)}` : ''
    } tires here. Shop by tire width, aspect ratio and rim size across tire brands, types and fitments here or at one of our 950+ locations.`;
  }

  if (
    queryType === QUERY_TYPES.SIZE_SEARCH &&
    productType === PRODUCT_TYPES.WHEELS
  ) {
    const { front, rear } = wheelSizes || {};
    let sizeText = formatWheelSizeParts(front) || frontWheelDiameter;
    if (rear) {
      sizeText = `${formatWheelSizeParts(front).replace(
        /\s+/g,
        ''
      )} X ${formatWheelSizeParts(rear).replace(/\s+/g, '')}`;
    } else if (rearWheelDiameter !== frontWheelDiameter) {
      sizeText = `${frontWheelDiameter} X ${rearWheelDiameter}`;
    }
    return `Find our selection of ${sizeText}${
      brand ? ` ${getBrandDisplayText(brand)}` : ''
    } wheels here. Shop by wheel diameter, width and bolt pattern across wheel brands, types and fitments here or at one of our 950+ locations.`;
  }

  if (queryParams.text) {
    return `Search results for ${queryParams.text} on ${site}`;
  }

  return site;
}

export function getPageBreadcrumbs({
  categoryData,
  productType,
  queryParams,
  queryType,
  routeParams
}) {
  let breadcrumbs = [];
  const { rearSize, size } = routeParams || {};
  const { boltPattern, diameter, width } = queryParams || {};

  if (
    (queryType === 'CATEGORY_SEARCH' || queryType === 'BRAND_SEARCH') &&
    typeof productType === 'string'
  ) {
    const productTypeUrl = `/${productType.toLowerCase()}`;
    const { superCategories } = categoryData;
    if (categoryData?.name) {
      breadcrumbs = [
        { name: productType.toLowerCase(), url: productTypeUrl },
        ...(productType === PRODUCT_TYPES.ACCESSORIES &&
        superCategories?.[0]?.name !== 'Accessories'
          ? [
              {
                name: superCategories?.[0]?.name,
                url: superCategories?.[0]?.url
              }
            ]
          : []),
        { name: categoryData.name }
      ];
    }
  }

  if (queryType === 'SIZE_SEARCH' && productType === PRODUCT_TYPES.TIRES) {
    breadcrumbs = [
      { name: 'Tires', url: '/tires' },
      {
        name: formatTireSizeParts(
          getTireSizes({ productType, rearSize, size })?.front
        )
      }
    ];
  }

  if (queryType === 'SIZE_SEARCH' && productType === PRODUCT_TYPES.WHEELS) {
    breadcrumbs = [
      { name: 'Wheels', url: '/wheels' },
      {
        name: formatWheelSizeParts(
          getWheelSizes({
            boltPattern,
            diameter,
            productType,
            rearSize,
            size,
            width
          })?.front
        )
      }
    ];
  }

  if (queryParams.text && typeof queryParams.text === 'string') {
    breadcrumbs = [
      { name: 'Home', url: '/' },
      { name: `Search for "${queryParams.text}"` }
    ];
  }

  return breadcrumbs;
}

export function getTireSizes({ productType, rearSize, size }) {
  if (productType !== PRODUCT_TYPES.TIRES) {
    return null;
  }
  return {
    front:
      typeof size === 'string'
        ? parseTireSize(decodeURIComponent(size))
        : undefined,
    rear:
      typeof rearSize === 'string'
        ? parseTireSize(decodeURIComponent(rearSize))
        : undefined
  };
}

export function getTitle({
  brand,
  category,
  productType,
  queryParams,
  queryType,
  selectedFitment,
  tireSizes,
  wheelSizes
}) {
  const { frontTireSize, frontWheelDiameter, rearTireSize, rearWheelDiameter } =
    selectedFitment || {};
  const site = window.siteName;
  if (
    queryType === QUERY_TYPES.CATEGORY_SEARCH ||
    queryType === QUERY_TYPES.BRAND_SEARCH
  ) {
    const categoryName = category?.name || '';

    let type = '';
    if (productType === PRODUCT_TYPES.TIRES) {
      type = 'Tires';
    } else if (productType === PRODUCT_TYPES.WHEELS) {
      type = 'Wheels';
    }

    if (queryType === QUERY_TYPES.BRAND_SEARCH && queryParams?.cat) {
      if (/atv|utv/i.test(queryParams.cat)) {
        type = `ATV/UTV ${type}`;
      } else {
        type = getBrandDisplayText(queryParams.cat);
      }

      if (/All /.test(type)) type = type.replace('All ', 'All-');

      return `${categoryName.replace(
        /\sTires|\sWheels$/i,
        ''
      )} ${type} | ${site}`;
    }
    if (categoryName === CATEGORY_NAMES.TIRE_CHAINS) {
      return `Buy the Best ${categoryName} Online | ${site}`;
    }
    if (categoryName === CATEGORY_NAMES.TIRE_CHAIN_TENSIONERS) {
      return `Buy ${categoryName} Online | ${site}`;
    }
    if (categoryName === CATEGORY_NAMES.TPMS_RETROFIT_KIT) {
      return `TPMS Retrofit Systems Kit Online | ${site}`; //Note unless we change the way CategoryData comes back for this it has to be typed out Since it comes back as "TPMS RetroFit Kit"
    }
    if (categoryName === CATEGORY_NAMES.TPMS_SENSORS) {
      return `${categoryName} Online | Tire Pressure Sensor | ${site}`;
    }
    if (categoryName === CATEGORY_NAMES.WINDSHIELD_WIPERS) {
      return `Buy Wiper Blades Online | ${site}`;
    }

    return [categoryName, type, site].join(' | ');
  }

  if (
    queryType === QUERY_TYPES.SIZE_SEARCH &&
    productType === PRODUCT_TYPES.TIRES
  ) {
    const { front, rear } = tireSizes || {};
    return `${formatTireSizeParts(front).replace(/\s+/g, '') || frontTireSize}${
      rear || rearTireSize !== frontTireSize
        ? ' X ' +
          (formatTireSizeParts(rear).replace(/\s+/g, '') || rearTireSize)
        : ''
    }${brand ? ` ${getBrandDisplayText(brand)}` : ''} Tires | ${site}`;
  }

  if (
    queryType === QUERY_TYPES.SIZE_SEARCH &&
    productType === PRODUCT_TYPES.WHEELS
  ) {
    const { front, rear } = wheelSizes || {};
    return `${
      formatWheelSizeParts(front).replace(/\s+/g, '') || frontWheelDiameter
    }${
      rear || rearWheelDiameter !== frontWheelDiameter
        ? ' X ' +
          (formatWheelSizeParts(rear).replace(/\s+/g, '') || rearWheelDiameter)
        : ''
    }${brand ? ` ${getBrandDisplayText(brand)}` : ''} Wheels | ${site}`;
  }

  if (queryParams.text) {
    return `Search ${queryParams.text} | ${site}`;
  }

  return site;
}

export function getWheelSizes({
  boltPattern,
  diameter,
  productType,
  rearSize,
  size,
  width
}) {
  if (productType !== PRODUCT_TYPES.WHEELS) {
    return null;
  }
  if (typeof size === 'string') {
    return {
      front: parseWheelSize(decodeURIComponent(size)),
      rear: parseWheelSize(decodeURIComponent(rearSize))
    };
  }

  if (diameter || width || boltPattern) {
    return {
      front: {
        boltPattern: boltPattern ?? '',
        diameter: diameter ?? '',
        width: width ?? ''
      }
    };
  }

  return null;
}

export function updateSuggestedSellingSearchedStatus(productType, queryParams) {
  const { text } = queryParams || {};
  const prevSuggestedSellingValues =
    localStorage(SUGGESTED_SELLING_VALUES) || DEFAULT_SUGGESTED_SELLING_VALUES;
  let updatedSuggestedSellingValues = { ...prevSuggestedSellingValues };

  if (productType) {
    if (productType === PRODUCT_TYPES.TIRES) {
      updatedSuggestedSellingValues.browse.isTire = true;
    } else if (productType === PRODUCT_TYPES.WHEELS) {
      updatedSuggestedSellingValues.browse.isWheel = true;
    } else if (productType === PRODUCT_TYPES.ACCESSORIES) {
      updatedSuggestedSellingValues.browse.isAccessory = true;
    }
  } else if (text) {
    if (/tire/.test(text)) {
      updatedSuggestedSellingValues.isTireSearched = true;
    } else if (/wheel/.test(text)) {
      updatedSuggestedSellingValues.isWheelSearched = true;
    } else if (/accessor/.test(text)) {
      updatedSuggestedSellingValues.isAccessorySearched = true;
    }
  }

  localStorage(SUGGESTED_SELLING_VALUES, updatedSuggestedSellingValues);
}

export function searchBySizeUrl(productType, selectedFitment) {
  let baseUrl = '/fitmentresult';
  let size;
  switch (productType) {
    case PRODUCT_TYPES.TIRES:
      baseUrl += '/tires/size/';
      size = selectedFitment.frontTireSize.match(/\d+/g).join('-');
      if (selectedFitment.isStaggered) {
        size += '/rear/' + selectedFitment.rearTireSize.match(/\d+/g).join('-');
      }
      return baseUrl + size;

    case PRODUCT_TYPES.WHEELS:
      if (!selectedFitment.isStaggered) {
        baseUrl += '/wheels/searchBySize?diameter=';
        size = selectedFitment.frontWheelDiameter;
      }
      if (selectedFitment.isStaggered) {
        baseUrl += '/wheels/size/';
        size =
          selectedFitment.frontWheelDiameter +
          '/rear/' +
          selectedFitment.rearWheelDiameter;
      }
      return baseUrl + size;
  }
}

export function getQueryParamsFromStaggeredSearch(
  category,
  queryType,
  productType,
  selectedFilters
) {
  const queryParams = selectedFilters.reduce(
    (acc, next) => acc + `:${next.categoryCode}:${next.valueCode}`,
    ''
  );

  switch (queryType) {
    case QUERY_TYPES.BRAND_SEARCH: {
      return `?q=:bestSeller-asc:brands:brand-${category.code.substr(
        category.code.length - 3
      )}${queryParams}`;
    }
    case QUERY_TYPES.CATEGORY_SEARCH: {
      const type =
        productType === PRODUCT_TYPES.TIRES ? 'tireCategory' : 'wheelCategory';
      return `?q=:bestSeller-asc:${type}:${category.code}${queryParams}`;
    }
    default: {
      return '';
    }
  }
}
